import React from 'react';
import get from 'lodash/get';
import set from 'lodash/set';
import styled from 'styled-components';

import Checkbox from 'src/presentation/components/forms/Checkbox';
import {
  ColumnRow,
  CustomRow,
  DefaultSection,
  FixedWidthContentRow,
  HalfRow,
  HeaderPosition,
  InputType,
  InputsBlock,
  SelectorContainer,
  FixedWidthContentColumn,
} from 'src/presentation/components/pages/chat/ChatLayout';
import {
  DEFAULTS_FOR_NEW_CONFIGURATION,
  FIELDS_MAPPER,
  GREETING_DESCRIPTION,
  GREETING_DISABLED_ON_BOARDING_BLOCK_INPUTS,
  GREETING_ENABLED_ON_BOARDING_BLOCK_INPUTS,
  ONBOARDING_PROMPT_ORDER,
  ONBOARDING_RESPONSE_TYPES,
} from 'src/presentation/components/pages/chat/chatConstants';
import { ControlDescription } from '@ad-scripts/presentation/components/common/forms/control_container';
import { getSelectorOptions } from 'src/presentation/components/pages/shared/utils/Utils';
import { getSvgImage } from './GreetingBlockLayout';
import { InputSize } from 'src/presentation/components/forms/InputSize';
import QuickReplyInputs from './QuickReplyInputs';
import RadioGroup from 'src/presentation/components/forms/RadioGroup';
import ReversedValueCheckbox from 'src/presentation/components/pages/shared/ReversedValueCheckbox';
import Textarea from 'src/presentation/components/forms/Textarea';
import HtmlEditor from '../components/HtmlEditor';
import useIsFeatureEnabled from 'src/presentation/hooks/useIsFeatureEnabled';

const GreetingBlock = ({ workingState, updateState, errors, resetError, isMobile = false, isHtmlEditorEnabled }) => {
  const onboardingEnabled = get(workingState, FIELDS_MAPPER.ONBOARDING_ENABLED.path, false);
  const isFeatureEnabled = useIsFeatureEnabled();
  const allowQuickRepliesInClosedConversation = isFeatureEnabled('allowQuickRepliesInClosedConversation');
  return (
    <DefaultSection
      svgImage={getSvgImage({ workingState, isMobile })}
      headerPosition={HeaderPosition.INSIDE_BLOCK}
      header="Greeting"
      description={GREETING_DESCRIPTION}
    >
      <FixedWidthContentColumn>
        <OnboardDropDownsSection>
          <FixedWidthContentRow>{onboardingEnabledCheckbox()}</FixedWidthContentRow>
          <FixedWidthContentRow>{promptEmailOrNameOrder(onboardingEnabled)}</FixedWidthContentRow>
        </OnboardDropDownsSection>
        <FixedWidthContentRow>
          {renderGreetingOnboardingBlock({
            workingState,
            updateState,
            errors,
            resetError,
            onboardingEnabled,
            isHtmlEditorEnabled,
            allowQuickRepliesInClosedConversation,
          })}
        </FixedWidthContentRow>
      </FixedWidthContentColumn>
    </DefaultSection>
  );
};

const onboardingEnabledCheckbox = () => (
  <HalfRow>
    <SelectorContainer>
      <StyledCheckbox
        className="checkbox"
        attr={FIELDS_MAPPER.ONBOARDING_ENABLED.path}
        data-aid={FIELDS_MAPPER.ONBOARDING_ENABLED.path}
        label="Onboarding"
      />
      <CheckboxControlDescription>
        {
          'If the checkbox is selected, Gladly will attempt to collect the customer’s email and name. If not, Gladly will not ask the customer for their email and name.'
        }
      </CheckboxControlDescription>
    </SelectorContainer>
  </HalfRow>
);

const promptEmailOrNameOrder = onboardingEnabled => {
  if (!onboardingEnabled) {
    return null;
  }

  const dropdownOptions = getSelectorOptions(ONBOARDING_PROMPT_ORDER);

  return (
    <HalfRow>
      <SelectorContainer>
        <RadioGroup
          attr={FIELDS_MAPPER.ONBOARDING_COMPOSITION_STAGE.path}
          data-aid={FIELDS_MAPPER.ONBOARDING_COMPOSITION_STAGE.path}
          description={FIELDS_MAPPER.ONBOARDING_COMPOSITION_STAGE.description}
          label="Prompt order"
          options={dropdownOptions}
        />
      </SelectorContainer>
    </HalfRow>
  );
};

function renderGreetingOnboardingBlock({
  workingState,
  updateState,
  errors,
  resetError,
  onboardingEnabled,
  isHtmlEditorEnabled,
  allowQuickRepliesInClosedConversation,
}) {
  const autoResponseMessageDisabled = get(workingState, FIELDS_MAPPER.DISABLE_AUTO_RESPONSE_MESSAGE.path, false);

  const controlsList = onboardingEnabled
    ? GREETING_ENABLED_ON_BOARDING_BLOCK_INPUTS
    : GREETING_DISABLED_ON_BOARDING_BLOCK_INPUTS;
  const inputType = isHtmlEditorEnabled ? InputType.TEXT_AREA_HTML : InputType.TEXT_AREA;
  return (
    <HalfRow>
      <ColumnRow>
        {InputsBlock({
          workingState,
          controlsList,
          inputType: inputType,
          size: InputSize.XLARGE,
        })}
        <>
          <FixedWidthContentRow>{onboardingResponseTypeDropdown(workingState, updateState)}</FixedWidthContentRow>
          <FixedWidthContentRow style={{ width: InputSize.XLARGE }}>
            <QuickReplyInputs errors={errors} resetError={resetError} />
          </FixedWidthContentRow>
          <CustomRow style={{ width: InputSize.XLARGE }}>
            {showQuickReplyButtonsWhenChatIsEndedCheckbox(workingState, allowQuickRepliesInClosedConversation)}
          </CustomRow>
        </>
        <CustomRow style={{ width: InputSize.XLARGE }}>
          <LastMessage
            autoResponseMessageDisabled={autoResponseMessageDisabled}
            onboardingEnabled={onboardingEnabled}
            isHtmlEditorEnabled={isHtmlEditorEnabled}
          />
        </CustomRow>
      </ColumnRow>
    </HalfRow>
  );
}

const LastMessage = ({ onboardingEnabled, autoResponseMessageDisabled, isHtmlEditorEnabled }) => {
  return (
    <>
      <div>
        <StyledReversedValueCheckbox
          className="checkbox"
          attr={FIELDS_MAPPER.DISABLE_AUTO_RESPONSE_MESSAGE.path}
          data-aid={FIELDS_MAPPER.DISABLE_AUTO_RESPONSE_MESSAGE.path}
          label={onboardingEnabled ? 'Fourth Message' : 'Second Message'}
        />
        <CheckboxControlDescription>
          {FIELDS_MAPPER.DISABLE_AUTO_RESPONSE_MESSAGE.description}
        </CheckboxControlDescription>

        {LastMessageTextArea({ autoResponseMessageDisabled, isHtmlEditorEnabled })}
      </div>
    </>
  );
};

const LastMessageTextArea = ({ autoResponseMessageDisabled, isHtmlEditorEnabled }) => {
  if (autoResponseMessageDisabled) {
    return null;
  }
  if (isHtmlEditorEnabled) {
    return <HtmlEditor editorPath={FIELDS_MAPPER.CUSTOM_TEXT_FOURTH_MESSAGE.path} placeholder={'Enter Text...'} />;
  }
  return (
    <StyledTextarea
      data-aid={FIELDS_MAPPER.CUSTOM_TEXT_FOURTH_MESSAGE.path}
      key={`textarea.${FIELDS_MAPPER.CUSTOM_TEXT_FOURTH_MESSAGE.path}`}
      attr={FIELDS_MAPPER.CUSTOM_TEXT_FOURTH_MESSAGE.path}
    />
  );
};

function showQuickReplyButtonsWhenChatIsEndedCheckbox(workingState, allowQuickRepliesInClosedConversation) {
  if (!allowQuickRepliesInClosedConversation) {
    return null;
  }
  const userResponseType: string = get(workingState, FIELDS_MAPPER.ONBOARDING_RESPONSE_TYPE.path, 'TEXT');
  if (userResponseType !== 'QUICK_REPLY_RESPONSE') {
    return null;
  }
  return (
    <StyledCheckbox
      className="checkbox"
      attr={FIELDS_MAPPER.SHOW_QUICK_REPLY_WHEN_CHAT_ENDED.path}
      data-aid={FIELDS_MAPPER.SHOW_QUICK_REPLY_WHEN_CHAT_ENDED.path}
      label={'Show Quick Reply buttons when the Chat is ended'}
    />
  );
}
function onboardingResponseTypeDropdown(workingState, updateState) {
  const dropdownOptions = getSelectorOptions(ONBOARDING_RESPONSE_TYPES);

  const onChange = ev => {
    const value = ev.target.value;

    updateState(set({}, FIELDS_MAPPER.ONBOARDING_RESPONSE_TYPE.path, value));

    const quickReplies = get(workingState, FIELDS_MAPPER.QUICK_REPLY_OPTIONS.path);
    if (value === 'QUICK_REPLY_RESPONSE' && quickReplies.length == 0) {
      updateState(
        set(
          {},
          FIELDS_MAPPER.QUICK_REPLY_OPTIONS.path,
          DEFAULTS_FOR_NEW_CONFIGURATION.SUB_TYPE_CONTENT.defaultValue.options
        )
      );
    }
    if (value === 'TEXT') {
      updateState(set({}, FIELDS_MAPPER.SHOW_QUICK_REPLY_WHEN_CHAT_ENDED.path, false));
    }
  };
  return (
    <HalfRow>
      <RadioGroup
        attr={FIELDS_MAPPER.ONBOARDING_RESPONSE_TYPE.path}
        data-aid={FIELDS_MAPPER.ONBOARDING_RESPONSE_TYPE.path}
        description={FIELDS_MAPPER.ONBOARDING_RESPONSE_TYPE.description}
        options={dropdownOptions}
        onChange={onChange}
      />
    </HalfRow>
  );
}

const OnboardDropDownsSection = styled.div``;

const CheckboxControlDescription = styled(ControlDescription)`
  margin-left: ${p => p.theme.spacing.insetLarge};
`;

const StyledTextarea = styled(Textarea)`
  margin-left: ${p => p.theme.spacing.insetLarge};
`;

const StyledReversedValueCheckbox = styled(ReversedValueCheckbox)`
  span {
    font-weight: ${p => p.theme.fontWeight.heavy};
  }
`;

const StyledCheckbox = styled(Checkbox)`
  span {
    font-weight: ${p => p.theme.fontWeight.heavy};
  }
`;

export default GreetingBlock;
